import { useSessionStore } from "../../../state/src/useSessionStore";
import { mapiGetCountryDetails } from "../services/mapi.service";

const inflight = new Map<string, Promise<any[]>>();

function normalizeCountryKey(iso3: string): string {
  return (iso3 || "").trim().toUpperCase();
}

/**
 * Store subdivisions for a country without calling the API (e.g. after country step already fetched details).
 */
export function primeMapiSubdivisionsCache(
  iso3: string,
  subdivisions: unknown
): void {
  const key = normalizeCountryKey(iso3);
  if (!key) return;
  const list = Array.isArray(subdivisions) ? subdivisions : [];
  (useSessionStore.getState() as any).setMapiSubdivisionsForCountry?.(key, list);
}

/**
 * Returns subdivisions for an ISO-3166-1 alpha-3 country code from session cache, or fetches
 * via `mapiGetCountryDetails` and caches the result (same source as {@link MapiChooseStateStep}).
 */
export async function getOrFetchMapiSubdivisions(iso3: string): Promise<any[]> {
  const key = normalizeCountryKey(iso3);
  if (!key) return [];

  const snap = useSessionStore.getState() as any;
  const cache: Record<string, any[]> = snap.mapiSubdivisionsByCountryCode || {};
  if (Object.prototype.hasOwnProperty.call(cache, key)) {
    return cache[key];
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const details = await mapiGetCountryDetails(key);
      const subdivisions =
        details.success && details.data
          ? details.data[0]?.subdivisions
          : [];
      const list = Array.isArray(subdivisions) ? subdivisions : [];
      (useSessionStore.getState() as any).setMapiSubdivisionsForCountry?.(
        key,
        list
      );
      return list;
    } catch (e) {
      console.error("getOrFetchMapiSubdivisions failed", key, e);
      (useSessionStore.getState() as any).setMapiSubdivisionsForCountry?.(
        key,
        []
      );
      return [];
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}
